import { ComponentProps, ReactNode } from 'react';
import { withStyles, Title2 as VTitle, CircleButton } from 'vopay';
import styles from './Dialog.module.scss';
import { Dialog as RDialog, VisuallyHidden } from 'radix-ui';

const cn = withStyles(styles);

type DialogRootProps = {
  isOpen: boolean;
  children: ReactNode;
  onClose: () => void;
};

export default function Dialog({ isOpen, children, onClose }: DialogRootProps) {
  return (
    <RDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {children}
    </RDialog.Root>
  );
}

type DialogContentProps = {
  children: ReactNode;
  closeButton?: boolean;
  closeOnOutsideClick?: boolean;
  closeOnEscape?: boolean;
  // Strips the content's own card chrome (background, padding, radius, shadow, fixed width), leaving a
  // centering-only wrapper like the host portals' placeholder useDialog — the caller supplies its own
  // surface. Centering + z-index are kept, so a position:fixed child (e.g. the eLinx iframe overlay)
  // is still contained and centered.
  bare?: boolean;
};

function Content({
  children,
  closeButton = false,
  closeOnOutsideClick = false,
  closeOnEscape = false,
  bare = false,
}: DialogContentProps) {
  return (
    <>
      <RDialog.Overlay className={cn('overlay')} />

      <RDialog.Content
        onInteractOutside={(e) => {
          if (!closeOnOutsideClick) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!closeOnEscape) {
            e.preventDefault();
          }
        }}
        className={cn('content', bare && 'bare')}
        // No Description concept in this dialog; without this Radix warns about the missing one.
        aria-describedby={undefined}
      >
        {closeButton && (
          <RDialog.Close asChild>
            <CircleButton className={cn('closeButton')} icon="close" circleFill={false} />
          </RDialog.Close>
        )}

        {children}
      </RDialog.Content>
    </>
  );
}

Dialog.Content = Content;

Dialog.Trigger = RDialog.Trigger;

type TitleProps = ComponentProps<typeof VTitle> & {
  hidden?: boolean;
};

// The dialog's accessible name. Every component opened through useDialog() must render one — where it
// wants it, or `hidden` (VisuallyHidden) when the design has no visible heading. Radix wires it to
// aria-labelledby and logs an error on open if it's missing.
function Title({ hidden, ...props }: TitleProps) {
  if (hidden) {
    return (
      <VisuallyHidden.Root>
        <RDialog.Title asChild>
          <VTitle {...props} />
        </RDialog.Title>
      </VisuallyHidden.Root>
    );
  }

  return (
    <RDialog.Title asChild>
      <VTitle level="2" {...props} />
    </RDialog.Title>
  );
}

Dialog.Title = Title;
